import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Calendar, ShieldCheck, Brain, ArrowUpRight, Receipt,
  MessageCircle, TrendingUp, Check, Wand2, Zap, Lock,
  Cloud, Users, ChevronDown, IndianRupee, FileText, Phone,
  Building2, Menu, X,
} from "lucide-react";
import { toast } from "sonner";
import AIReceptionistWidget from "@/components/app/AIReceptionistWidget";
import HeroProductLoop from "@/components/HeroProductLoop";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { appUrl } from "@/lib/domains";

/* ---------- SEO structured data (SoftwareApplication + FAQPage + Organization) ---------- */
const SOFTWARE_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Viyora",
  "applicationCategory": "BusinessApplication",
  "applicationSubCategory": "Dental Practice Management Software",
  "operatingSystem": "Web",
  "description":
    "Viyora is an AI-powered clinic management platform for dentists and healthcare practices — appointments, patient records, GST billing, prescriptions, digital consent, X-ray storage, inventory, WhatsApp reminders, analytics and AI Copilot. 30-day free trial. DPDP Act 2023 compliant.",
  "offers": [
    { "@type": "Offer", "name": "Solo", "price": "799", "priceCurrency": "INR", "billingIncrement": "P1M" },
    { "@type": "Offer", "name": "Clinic", "price": "1499", "priceCurrency": "INR", "billingIncrement": "P1M" },
  ],
  "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "132" },
  "publisher": { "@type": "Organization", "name": "Viyora Tech", "url": "https://viyora.co.in" },
  "featureList": [
    "GST-compliant PDF invoicing",
    "Drag-and-drop appointment calendar",
    "WhatsApp appointment reminders",
    "Viyora Copilot with unlimited requests",
    "Digital consent forms",
    "Encrypted X-ray cloud storage",
    "Inventory with treatment kits",
    "Multi-tenant with team roles",
    "DPDP Act 2023 compliant",
  ],
};

const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Viyora Tech",
  "url": "https://viyora.co.in",
  "logo": "https://viyora.co.in/viyora-icon-512.png",
  "sameAs": [],
  "contactPoint": [{
    "@type": "ContactPoint",
    "email": "admin@viyora.co.in",
    "contactType": "customer support",
    "areaServed": "IN",
    "availableLanguage": ["English", "Hindi"],
  }],
};

const FAQS = [
  {
    q: "Why choose Viyora over other Indian dental software?",
    a: "Viyora is India-first: GST-compliant PDF invoicing, WhatsApp reminders on the official Business API, INR pricing, DPDP Act 2023 compliance, and a 24/7 Viyora Copilot. Most legacy tools were built for the US market or as generic clinic software — Viyora is built here, for here, specifically for dental practice.",
  },
  {
    q: "How does Viyora reduce no-shows?",
    a: "Automated WhatsApp reminders on the official WhatsApp Business API go out 24 hours and 2 hours before every appointment. Clinics using Viyora report a 35–45% drop in no-shows within the first quarter.",
  },
  {
    q: "Are my patient records safe?",
    a: "Every record is encrypted at rest and in transit. Access is role-scoped (admin, doctor, receptionist) with clinic-level isolation. We're fully compliant with the DPDP Act 2023 — no data ever leaves Indian servers.",
  },
  {
    q: "Do I need to install anything?",
    a: "No. Viyora runs entirely in the browser — Chrome, Safari, Edge, and on iPads and Android tablets for chairside consent forms.",
  },
  {
    q: "Can I import my existing patient database?",
    a: "Yes. We support one-click imports from Excel, CSV, and JSON. Our success team will help you migrate from any legacy dental or clinic-management software during your free trial.",
  },
  {
    q: "What's included in the 30-day free trial?",
    a: "Everything. Full Viyora Copilot, WhatsApp reminders (up to 500 messages), unlimited patients, GST invoicing, X-ray storage — no credit card required.",
  },
  {
    q: "Is Viyora DPDP Act 2023 compliant?",
    a: "Yes. Full data-portability (one-click JSON export), right-to-erasure, encrypted at rest with role-based access, breach-notification workflows, and a Data Fiduciary agreement signed on onboarding.",
  },
  {
    q: "Do you support GST-compliant invoicing?",
    a: "Yes — CGST/SGST/IGST split by state, HSN codes, sequential invoice numbers, and CA-ready PDF exports rendered by WeasyPrint. Any Indian CA will accept them without changes.",
  },
];

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

const STRUCTURED_DATA = { "@graph": [SOFTWARE_LD, ORG_LD, FAQ_LD] };

/* ---------- Motion helpers ---------- */
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.6, ease: [0.22, 0.61, 0.36, 1] },
};

const stagger = (i) => ({
  ...fadeUp,
  transition: { ...fadeUp.transition, delay: i * 0.08 },
});

/* ==================== LANDING ==================== */
export default function Landing() {
  const subscribe = async (lookupKey) => {
    // Auth is now a cookie — we can't peek at it from JS. Instead, make a
    // cheap authenticated call; if it 401s, the user is signed out.
    try {
      await api.get("/auth/me");
    } catch {
      toast.info("Sign in to your clinic first, then subscribe.");
      window.location.href = "/login";
      return;
    }
    await openRazorpayCheckout({ lookup_key: lookupKey });
  };

  return (
    <div className="min-h-screen bg-linen text-ink font-sans">
      <SEO
        path="/"
        title="Viyora – AI-powered Clinic Management Software"
        description="Viyora is an AI-powered clinic management platform for dentists and healthcare practices. Manage appointments, patient records, GST billing, inventory, WhatsApp reminders and analytics from one secure platform."
        keywords="Viyora, clinic management software, dental clinic software India, GST dental invoice software, WhatsApp appointment reminders for dentists, AI clinic management, dental EMR India, cloud clinic software"
        structuredData={STRUCTURED_DATA}
      />

      <Nav />
      <Hero />
      <WhatIsViyora />
      <TrustBar />
      <ROICalculator />
      <Features />
      <HowItWorks />
      <Pricing subscribe={subscribe} />
      <Testimonials />
      <FAQ />
      <ContactSection />
      <FinalCTA />
      <Footer />
      <ContactSupportFab />
      <AIReceptionistWidget />
    </div>
  );
}

/* ==================== SECTIONS ==================== */

function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  return (
    <header className="sticky top-0 z-40 bg-linen/80 backdrop-blur-md border-b border-[#E2DCD0]" data-testid="landing-nav">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[76px] md:h-[92px] flex items-center justify-between gap-2">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 md:gap-4 shrink-0 group">
          {/* Logo — served from the transparent PNG master. `srcset` uses the
              512-px asset so retina displays get a pixel-perfect render even
              at the smaller wordmark-scale used on mobile. */}
          <img
            src="/viyora-icon-256.png"
            srcSet="/viyora-icon-64.png 1x, /viyora-icon-256.png 2x, /viyora-icon-512.png 4x"
            alt="Viyora logo"
            className="h-10 md:h-14 w-auto select-none transition-transform group-hover:scale-[1.03]"
            width="512" height="441"
            decoding="async"
          />
          <span className="font-serif text-2xl md:text-[40px] leading-none text-ink tracking-[-0.02em] font-medium">
            Viyora
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-sm text-ink/80">
          <a href="#features" className="hover:text-ink transition-colors">Features</a>
          <a href="#pricing" className="hover:text-ink transition-colors">Pricing</a>
          <Link to="/blog" data-testid="nav-blog" className="hover:text-ink transition-colors">Blog</Link>
          <a href="#contact" className="hover:text-ink transition-colors">Contact</a>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <a href="tel:+919810453892" data-testid="nav-phone"
             aria-label="Call Viyora on +91 9810453892"
             className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-ink/80 border border-pine/25 hover:border-pine rounded-full px-3 py-1.5 transition-colors">
            <Phone className="w-3.5 h-3.5" />
            +91 9810453892
          </a>
          <a href={appUrl("/login")} data-testid="nav-login"
            className="hidden sm:inline-block text-sm font-medium text-ink/90 hover:text-ink transition-colors">Sign in</a>
          <a href={appUrl("/signup")} data-testid="nav-get-started"
            className="inline-flex items-center gap-1.5 rounded-full bg-pine text-linen text-xs sm:text-sm font-semibold px-3.5 sm:px-5 py-2 hover:bg-pine/90 transition-colors whitespace-nowrap">
            Start trial <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border border-pine/25 text-ink active:bg-pine/5"
          >
            {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          className="md:hidden border-t border-[#E2DCD0] bg-linen/95 backdrop-blur-md"
          data-testid="nav-mobile-drawer"
        >
          <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col text-[15px] text-ink/85">
            <a href="#features" onClick={closeMenu} className="py-3 border-b border-[#E2DCD0] flex items-center justify-between">Features <ChevronDown className="w-4 h-4 -rotate-90 text-ink-soft" /></a>
            <a href="#pricing" onClick={closeMenu} className="py-3 border-b border-[#E2DCD0] flex items-center justify-between">Pricing <ChevronDown className="w-4 h-4 -rotate-90 text-ink-soft" /></a>
            <Link to="/blog" onClick={closeMenu} data-testid="nav-mobile-blog" className="py-3 border-b border-[#E2DCD0] flex items-center justify-between">
              Blog <ChevronDown className="w-4 h-4 -rotate-90 text-ink-soft" />
            </Link>
            <a href="#contact" onClick={closeMenu} className="py-3 border-b border-[#E2DCD0] flex items-center justify-between">Contact <ChevronDown className="w-4 h-4 -rotate-90 text-ink-soft" /></a>
            <a href={appUrl("/login")} onClick={closeMenu} data-testid="nav-mobile-signin" className="py-3 border-b border-[#E2DCD0] flex items-center justify-between">
              Sign in <ChevronDown className="w-4 h-4 -rotate-90 text-ink-soft" />
            </a>
            <a href="tel:+919810453892" onClick={closeMenu} className="py-3 flex items-center gap-2 text-ink">
              <Phone className="w-4 h-4" /> +91 9810453892
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

function Hero() {
  // NOTE: Hero copy is optimised for Google OAuth Branding Verification —
  // the reviewer must be able to identify the product name, target audience,
  // and functionality within the first screen.  The main heading is the
  // literal application name ("Viyora") to match the OAuth consent screen.
  // The prior marketing headline lives further down as a section title.
  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      {/* Ambient tint */}
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#D6C4B0]/40 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#B7D4C4]/40 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-16 sm:py-20 lg:py-28 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* LEFT — Text (col-span-5, giving the demo more room on the right) */}
          <div className="lg:col-span-5">
            <motion.div {...fadeUp}
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-[#E2DCD0] px-3.5 py-1.5 text-xs text-ink/80 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-clay" />
              Built in India for Modern Healthcare Practices
            </motion.div>

            <motion.div {...stagger(1)}
              data-testid="hero-eyebrow"
              className="text-[11px] uppercase tracking-[0.2em] font-semibold text-clay mb-3">
              AI-Powered Clinic Management Software
            </motion.div>

            <motion.h1 {...stagger(2)}
              data-testid="hero-title"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-ink">
              Viyora
            </motion.h1>

            <motion.p {...stagger(3)}
              data-testid="hero-subtitle"
              className="mt-4 text-lg sm:text-xl text-ink leading-snug max-w-xl">
              The complete clinic management platform for dentists and
              healthcare practices.
            </motion.p>

            <motion.p {...stagger(4)}
              data-testid="hero-description"
              className="mt-4 text-sm sm:text-base text-ink/75 max-w-xl leading-relaxed">
              Manage appointments, patient records, GST billing, prescriptions,
              digital consent, X-rays, inventory, WhatsApp reminders, analytics
              and AI-powered workflows from one secure cloud platform.
            </motion.p>

            <motion.p {...stagger(5)}
              data-testid="hero-operator"
              className="mt-3 text-xs text-ink-soft">
              Developed and operated by Viyora Tech
            </motion.p>

            <motion.div {...stagger(6)} className="mt-7 flex flex-wrap gap-3">
              <a href={appUrl("/signup")} data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-full bg-pine text-linen px-6 sm:px-7 py-3.5 text-[15px] font-semibold hover:bg-pine/90 transition-colors">
                Start Free Trial <ArrowUpRight className="w-4 h-4" />
              </a>
              <a href="mailto:admin@viyora.co.in?subject=Viyora%20demo%20request"
                 data-testid="hero-cta-secondary"
                 className="inline-flex items-center gap-2 rounded-full bg-white text-ink px-6 sm:px-7 py-3.5 text-[15px] font-semibold border border-[#E2DCD0] hover:border-pine transition-colors">
                Book a Demo
              </a>
            </motion.div>

            <motion.div {...stagger(7)}
              data-testid="hero-trust-indicators"
              className="mt-7 grid grid-cols-2 gap-x-5 gap-y-2 text-xs text-ink-mid max-w-md">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-clay" /> No credit card required</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-clay" /> DPDP Act 2023 Compliant</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-clay" /> Secure Cloud Platform</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-clay" /> Built for Indian Clinics</span>
            </motion.div>
          </div>

          {/* RIGHT — Auto-cycling real product demo */}
          <motion.div {...stagger(2)} className="lg:col-span-7 relative">
            <HeroProductLoop />
            {/* Floating proof card */}
            <div className="absolute -bottom-5 -right-3 sm:-right-4 bg-white rounded-2xl border border-[#E2DCD0] shadow-[0_12px_40px_rgba(26,54,43,0.10)] p-4">
              <div className="text-[10px] text-ink-mid uppercase tracking-wide font-medium">Today</div>
              <div className="font-serif text-2xl text-ink mt-1">14 reminders sent</div>
              <div className="text-xs text-clay mt-1 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> ₹42,850 revenue · +18%
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * WhatIsViyora — explicit "what is this application" section rendered
 * immediately below the hero.  Required for Google OAuth branding
 * verification: the reviewer must be able to identify the product, its
 * audience, and its capabilities without scrolling through marketing copy.
 * ------------------------------------------------------------------------- */
function WhatIsViyora() {
  const cards = [
    { icon: Calendar,      title: "Appointment Scheduling",     copy: "Drag-and-drop calendar with chair mapping, doctor rosters, and WhatsApp confirmations." },
    { icon: FileText,      title: "Electronic Patient Records", copy: "One source of truth for demographics, allergies, history, treatments, prescriptions, and X-rays." },
    { icon: Receipt,       title: "GST Billing",                copy: "GST-compliant PDF invoices, quotations, credit notes, and Razorpay-linked payments." },
    { icon: Building2,     title: "Inventory Management",       copy: "Real-time stock tracking, low-stock alerts, treatment kits, and expiry reminders." },
    { icon: Brain,         title: "AI Copilot",                 copy: "Viyora Copilot answers clinical, admin, and coding questions 24/7 with case-aware context." },
    { icon: MessageCircle, title: "WhatsApp Automation",        copy: "Reminders, follow-ups, and consent links delivered on the official WhatsApp Business API." },
    { icon: TrendingUp,    title: "Analytics",                  copy: "Revenue, chair utilisation, patient retention, and doctor performance — updated live." },
    { icon: Users,         title: "Multi-doctor Clinics",       copy: "Role-scoped access for admins, doctors, and receptionists across single or multi-branch practices." },
  ];
  return (
    <section id="what-is-viyora" className="py-16 sm:py-20 border-y border-[#E2DCD0] bg-white/60" data-testid="what-is-viyora">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.2em] text-clay font-semibold mb-3">Overview</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-ink"
              data-testid="what-is-viyora-heading">
            What is Viyora?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-ink/75 leading-relaxed">
            Viyora is a cloud-based clinic management software that helps doctors
            and clinics manage appointments, patient records, billing, inventory,
            communication and analytics from one secure platform.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
             data-testid="what-is-viyora-cards">
          {cards.map(({ icon: Icon, title, copy }) => (
            <div key={title}
                 data-testid={`what-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                 className="rounded-2xl border border-[#E2DCD0] bg-white p-5 hover:border-pine/40 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-pine/10 text-pine grid place-items-center mb-3">
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-serif text-lg text-ink leading-snug">{title}</div>
              <p className="mt-2 text-sm text-ink-mid leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-sm text-ink-mid">
          Sign in to Viyora to access these clinic-management features for your practice.
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------------------
 * ContactSection — dedicated "who runs Viyora and how to reach us" block.
 * Google OAuth reviewers look for a page/section that clearly names the
 * legal entity, support email, phone, and website; embedding it inline on
 * the homepage keeps a single canonical landing URL for the OAuth screen.
 * ------------------------------------------------------------------------- */
function ContactSection() {
  return (
    <section id="contact-info" className="py-16 sm:py-20" data-testid="contact-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-clay font-semibold mb-3">Contact</div>
        <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-ink">
          Talk to the team behind Viyora
        </h2>
        <p className="mt-3 text-base text-ink/75 max-w-2xl leading-relaxed">
          Viyora is a cloud-based clinic management platform developed and
          operated by Viyora Tech. Contact us for onboarding, product support,
          partnership enquiries and live demonstrations.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-[#E2DCD0] bg-white p-5" data-testid="contact-company">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Company</div>
            <div className="font-serif text-lg text-ink leading-snug">Viyora Tech</div>
            <div className="mt-1 text-xs text-ink-mid">Legal entity</div>
          </div>
          <div className="rounded-2xl border border-[#E2DCD0] bg-white p-5" data-testid="contact-email">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Email</div>
            <a href="mailto:admin@viyora.co.in"
               className="font-serif text-lg text-ink hover:text-pine leading-snug break-all">
              admin@viyora.co.in
            </a>
            <div className="mt-1 text-xs text-ink-mid">Response within 1 business day</div>
          </div>
          <div className="rounded-2xl border border-[#E2DCD0] bg-white p-5" data-testid="contact-phone">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Phone</div>
            <a href="tel:+919810453892"
               className="font-serif text-lg text-ink hover:text-pine leading-snug">
              +91 9810453892
            </a>
            <div className="mt-1 text-xs text-ink-mid">Mon–Sat · 10:00–19:00 IST</div>
          </div>
          <div className="rounded-2xl border border-[#E2DCD0] bg-white p-5" data-testid="contact-location">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">Location</div>
            <div className="font-serif text-lg text-ink leading-snug">Noida, India</div>
            <div className="mt-1 text-xs text-ink-mid">Head office</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const stack = [
    "GST-ready billing",
    "WhatsApp automation",
    "Razorpay payments",
    "Google Sign-in",
    "Secure cloud storage",
    "DPDP Act 2023",
  ];
  return (
    <section className="py-10 sm:py-14 border-y border-[#E2DCD0] bg-white/60" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-ink-soft font-medium mb-6 sm:mb-8">
          Everything your clinic needs, in one place
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-10 gap-y-4">
          {stack.map((name, i) => (
            <motion.div key={name} {...stagger(i)}
              className="text-ink-mid font-serif text-base sm:text-lg tracking-tight">
              {name}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Features: one concise, scannable grid ---------- */
function Features() {
  const cards = [
    {
      testid: "feature-calendar",
      icon: Calendar,
      title: "Smart Calendar",
      bullets: [
        "Drag-and-drop across chairs & doctors",
        "Blocks double-bookings automatically",
        "Chair-utilisation dashboard",
      ],
      // Subtle sage → cream gradient
      gradient: "linear-gradient(135deg, #F1EDE4 0%, #E7EEE7 100%)",
      accent: "#2D6A4F",
    },
    {
      testid: "feature-billing",
      icon: Receipt,
      title: "GST-ready Billing",
      bullets: [
        "One-click GST invoices (PDF)",
        "CGST / SGST / IGST + HSN codes",
        "Razorpay payment links out of the box",
      ],
      // Cream → warm sand
      gradient: "linear-gradient(135deg, #F5EFE3 0%, #EEE1CB 100%)",
      accent: "#B4693E",
    },
    {
      testid: "feature-whatsapp",
      icon: MessageCircle,
      title: "WhatsApp Automation",
      bullets: [
        "Automated 24h & 2h appointment reminders",
        "Post-op instructions sent instantly",
        "Prescriptions & records on WhatsApp",
      ],
      // Pine (dark) — kept for visual anchor
      gradient: "linear-gradient(135deg, #1A362B 0%, #244A3B 100%)",
      tone: "pine",
      accent: "#D06A42",
    },
    {
      testid: "feature-ai",
      icon: Brain,
      title: "24/7 Viyora Copilot",
      bullets: [
        "Answers patient queries in Hindi & English",
        "Drafts treatment plans from your notes",
        "Escalates emergencies to the on-call dentist",
      ],
      // Deep pine (dark) matching WhatsApp for visual balance
      gradient: "linear-gradient(135deg, #244A3B 0%, #1A362B 100%)",
      tone: "pine",
      accent: "#D06A42",
    },
    {
      testid: "feature-consent",
      icon: FileText,
      title: "Digital Consent & Post-op",
      bullets: [
        "Legally-binding e-signatures on tablet",
        "Post-op instructions auto-sent after every procedure",
        "Warranty cards with public verify link",
      ],
      // Soft mist
      gradient: "linear-gradient(135deg, #EFEEE8 0%, #E3E4DB 100%)",
      accent: "#2D6A4F",
    },
    {
      testid: "feature-storage",
      icon: Cloud,
      title: "Secure Cloud Storage",
      bullets: [
        "Encrypted X-ray & document storage",
        "Inline viewer with side-by-side compare",
        "DPDP-compliant Indian data residency",
      ],
      // Warm cream
      gradient: "linear-gradient(135deg, #F0E7D9 0%, #E5D6BF 100%)",
      accent: "#B4693E",
    },
  ];

  return (
    <section id="features" className="py-16 sm:py-20 lg:py-28" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10 sm:mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-medium mb-3">Everything in one login</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight">
            Run your clinic like a corporate hospital — without the overhead
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <FeatureCard key={c.testid} {...c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* Re-usable card — subtle gradient bg, bullet list, hover lift. */
function FeatureCard({ icon: Icon, title, bullets = [], tone = "linen", gradient, accent = "#2D6A4F", testid, index = 0 }) {
  const isDark = tone === "pine";
  const textCls = isDark ? "text-linen" : "text-ink";
  const bulletCls = isDark ? "text-linen/85" : "text-ink/80";
  const borderCls = isDark ? "border-white/10" : "border-[#E2DCD0]";

  return (
    <motion.article
      {...stagger(index)}
      data-testid={testid}
      style={gradient ? { background: gradient } : undefined}
      className={`${textCls} rounded-2xl border ${borderCls} p-6 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(26,54,43,0.14)] transition-all duration-300 relative overflow-hidden`}
    >
      {/* Soft accent glow in the top-right corner */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -right-16 w-40 h-40 rounded-full opacity-30 blur-2xl pointer-events-none"
        style={{ backgroundColor: accent }}
      />
      <div
        className={`w-11 h-11 rounded-xl grid place-items-center mb-5 relative ${isDark ? "bg-white/10" : "bg-white/70 backdrop-blur-sm"}`}
        style={{ color: accent }}
      >
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-serif text-xl mb-3 leading-tight relative">{title}</h3>
      <ul className="space-y-2 relative">
        {bullets.map((b) => (
          <li key={b} className={`flex gap-2 text-sm ${bulletCls}`}>
            <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: accent }} />
            <span className="leading-snug">{b}</span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Building2, title: "Sign up in 60 seconds", desc: "Enter your clinic name, phone, and one email. Google or WhatsApp OTP — pick your poison. No credit card." },
    { icon: Wand2, title: "6-step onboarding wizard", desc: "Your logo, GSTIN, working hours, doctors, chairs. Everything personalised. Guided by our success team over WhatsApp." },
    { icon: Users, title: "Invite your team", desc: "Admin, doctors, receptionists — each with the right role. They sign in with Google or Supabase Phone OTP." },
    { icon: Zap, title: "Go live", desc: "Start booking. WhatsApp reminders auto-schedule. Viyora Copilot online. Your patients feel the difference on day one." },
  ];
  return (
    <section id="how" className="py-24 lg:py-32" data-testid="how-it-works">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-medium mb-4">How it works</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.05] tracking-tight">
            From signup to your first booking in under 30 minutes
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <motion.div key={s.title} {...stagger(i)}
              className="bg-white rounded-3xl border border-[#E2DCD0] p-7 relative">
              <div className="absolute top-5 right-5 font-serif text-4xl text-linen text-ink-faint">0{i+1}</div>
              <div className="w-11 h-11 rounded-xl bg-linen border border-[#E2DCD0] grid place-items-center text-ink mb-5">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-xl text-ink mb-2">{s.title}</h3>
              <p className="text-ink-mid text-[14px] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ROICalculator() {
  const [apptsPerDay, setApptsPerDay] = useState(20);
  const [avgFee, setAvgFee] = useState(800);
  const [currentNoShow, setCurrentNoShow] = useState(20);

  const monthlyLoss = useMemo(() => apptsPerDay * 26 * avgFee * (currentNoShow / 100), [apptsPerDay, avgFee, currentNoShow]);
  const withViyoraLoss = useMemo(() => monthlyLoss * 0.55, [monthlyLoss]); // 45% reduction
  const savings = Math.round(monthlyLoss - withViyoraLoss);
  const yearlySavings = savings * 12;

  return (
    <section className="py-20 sm:py-24 lg:py-32" data-testid="roi-calculator">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <motion.div {...fadeUp} className="text-center mb-10 sm:mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-medium mb-4">ROI Calculator</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight max-w-3xl mx-auto">
            See what WhatsApp reminders save your clinic every month
          </h2>
        </motion.div>

        <motion.div {...fadeUp} className="bg-white rounded-3xl border border-[#E2DCD0] p-5 sm:p-8 lg:p-12 grid lg:grid-cols-5 gap-8 sm:gap-10 items-center">
          {/* Inputs */}
          <div className="lg:col-span-3 space-y-7 sm:space-y-8">
            <RangeInput label="Appointments per day" min={5} max={80} value={apptsPerDay} onChange={setApptsPerDay} testid="roi-appts-slider" suffix="patients" />
            <RangeInput label="Average consultation fee" min={200} max={5000} step={50} value={avgFee} onChange={setAvgFee} testid="roi-fee-slider" prefix="₹" />
            <RangeInput label="Current no-show rate" min={5} max={45} value={currentNoShow} onChange={setCurrentNoShow} testid="roi-noshow-slider" suffix="%" />
          </div>

          {/* Output */}
          <div className="lg:col-span-2 relative overflow-hidden bg-white ring-2 ring-pine rounded-2xl p-6 sm:p-8 shadow-[0_20px_60px_-24px_rgba(245,83,45,0.35)]">
            <div className="text-xs uppercase tracking-[0.15em] text-ink-soft font-semibold mb-2">Your monthly savings</div>
            <div className="font-serif text-5xl sm:text-6xl text-ink font-bold tracking-tight tabular-nums break-words" data-testid="roi-monthly-savings">
              ₹{savings.toLocaleString("en-IN")}
            </div>
            <div className="text-xs text-ink-soft mt-1 font-medium">per month with Viyora WhatsApp reminders</div>

            <div className="mt-6 pt-6 border-t border-[#EEF0F2] space-y-3 text-sm">
              <div className="flex justify-between items-baseline gap-2">
                <span className="text-ink-mid font-medium">Yearly savings</span>
                <span className="font-serif text-xl sm:text-2xl text-ink font-bold tabular-nums">₹{yearlySavings.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between items-baseline gap-2">
                <span className="text-ink-mid font-medium">Viyora cost (Clinic plan)</span>
                <span className="text-ink font-semibold tabular-nums">₹17,988/yr</span>
              </div>
              <div className="flex justify-between items-baseline gap-2 pt-3 border-t border-[#EEF0F2]">
                <span className="text-ink font-bold">Net gain</span>
                <span className="font-serif text-2xl sm:text-3xl text-pine font-extrabold tabular-nums">₹{(yearlySavings - 17988).toLocaleString("en-IN")}</span>
              </div>
            </div>

            <a href={appUrl("/signup")} data-testid="roi-cta"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-pine text-white px-5 py-3 text-sm font-semibold hover:bg-pine-mid transition-colors shadow-sm">
              Claim your savings — free trial
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function RangeInput({ label, min, max, value, onChange, step = 1, prefix = "", suffix = "", testid }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-sm font-medium text-ink/80">{label}</label>
        <span className="font-serif text-2xl text-ink">
          {prefix}{value.toLocaleString("en-IN")}{suffix && ` ${suffix}`}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full h-2 bg-linen rounded-full appearance-none cursor-pointer accent-clay"
      />
      <div className="flex justify-between text-[11px] text-ink-soft mt-1">
        <span>{prefix}{min}{suffix}</span><span>{prefix}{max}{suffix}</span>
      </div>
    </div>
  );
}

function Testimonials() {
  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-linen" data-testid="testimonials">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <motion.div {...fadeUp} className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-medium mb-3">Built on trust</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Your patient data, safer than a filing cabinet.
          </h2>
          <p className="mt-4 text-ink-mid max-w-2xl mx-auto text-base sm:text-lg">
            Every record encrypted, every access logged, and every byte stays in India — because DPDP compliance shouldn&apos;t be an afterthought.
          </p>
        </motion.div>

        {/* Compact security / trust strip */}
        <motion.div {...fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="trust-strip">
          {[
            { icon: ShieldCheck, title: "DPDP Act 2023", desc: "Signed data-fiduciary agreements" },
            { icon: Lock,        title: "AES-256 at rest", desc: "Every X-ray encrypted server-side" },
            { icon: Users,       title: "Role-based access", desc: "Admin · Doctor · Receptionist audit log" },
            { icon: Cloud,       title: "Indian data centres", desc: "Records never leave India" },
          ].map((b, i) => (
            <motion.div key={b.title} {...stagger(i)}
              className="bg-white rounded-2xl border border-[#E2DCD0] p-5">
              <b.icon className="w-5 h-5 text-clay mb-3" />
              <div className="font-serif text-base text-ink">{b.title}</div>
              <div className="text-xs text-ink-mid mt-0.5 leading-snug">{b.desc}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Pricing({ subscribe }) {
  const [yearly, setYearly] = useState(false);
  const soloM = 799, soloY = Math.round(7999 / 12);
  const clinicM = 1499, clinicY = Math.round(14999 / 12);

  return (
    <section id="pricing" className="py-20 sm:py-24 lg:py-32" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-6">
        <motion.div {...fadeUp} className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-medium mb-4">Pricing</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Simple, transparent pricing. No hidden charges.
          </h2>
          <p className="mt-5 text-lg text-ink-mid max-w-2xl mx-auto">30-day free trial on any plan. Cancel anytime. GST invoice for every payment.</p>

          <div className="inline-flex items-center bg-white border border-[#E2DCD0] rounded-full p-1 mt-8">
            <button data-testid="billing-monthly-btn"
              onClick={() => setYearly(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${!yearly ? "bg-pine text-linen" : "text-ink-mid"}`}>
              Monthly
            </button>
            <button data-testid="billing-yearly-btn"
              onClick={() => setYearly(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${yearly ? "bg-pine text-linen" : "text-ink-mid"}`}>
              Yearly <span className="ml-1 text-clay text-xs">save 16%</span>
            </button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-6 mt-10">
          {/* Solo */}
          <motion.div {...fadeUp} className="bg-white rounded-3xl border border-[#E5E7EB] p-6 sm:p-8 shadow-[0_1px_2px_rgba(17,24,39,0.04)] hover:shadow-[0_20px_48px_-24px_rgba(17,24,39,0.18)] transition-shadow flex flex-col" data-testid="plan-solo">
            <div className="text-xs uppercase tracking-[0.15em] text-ink-soft font-semibold mb-3">Solo</div>
            <div className="flex items-baseline gap-1">
              <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6 text-ink" />
              <span className="font-serif text-5xl sm:text-6xl text-ink font-bold tracking-tight tabular-nums">{yearly ? soloY : soloM}</span>
              <span className="text-ink-mid text-sm sm:text-base font-medium">/mo</span>
            </div>
            {yearly && <div className="text-xs text-pine font-bold mt-1">Billed ₹7,999/year · save ₹1,589</div>}
            <p className="text-sm text-ink-mid font-medium mt-4 leading-relaxed">Perfect for single-dentist practices getting started.</p>
            <ul className="mt-7 sm:mt-8 space-y-3 text-sm">
              {["Unlimited patients & appointments", "GST invoicing (PDF)", "WhatsApp reminders (500/mo)", "5 GB X-ray storage",
                "Viyora Copilot (100 requests/mo)", "Digital consent forms", "Email support"].map(f => (
                <li key={f} className="flex items-start gap-3 text-ink-mid font-medium leading-relaxed">
                  <span className="mt-0.5 grid place-items-center w-4 h-4 rounded-full shrink-0 bg-pine/10 text-pine">
                    <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                  </span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-solo-btn"
              onClick={() => subscribe(yearly ? "solo_yearly" : "solo_monthly")}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white border border-ink text-ink px-5 py-3 text-sm font-semibold hover:bg-ink hover:text-white transition-colors">
              Start free trial
            </button>
          </motion.div>

          {/* Clinic — highlighted */}
          <motion.div {...fadeUp} className="bg-white rounded-3xl p-6 sm:p-8 relative ring-2 ring-pine shadow-[0_1px_2px_rgba(17,24,39,0.04),0_24px_60px_-28px_rgba(245,83,45,0.35)] hover:shadow-[0_1px_2px_rgba(17,24,39,0.04),0_32px_72px_-24px_rgba(245,83,45,0.45)] transition-shadow flex flex-col" data-testid="plan-clinic">
            <div className="inline-flex self-start items-center bg-pine text-white text-[10px] uppercase tracking-[0.14em] font-bold px-3 py-1 rounded-full mb-3 shadow-sm">Most popular</div>
            <div className="text-xs uppercase tracking-[0.15em] text-ink-soft font-semibold mb-3">Clinic</div>
            <div className="flex items-baseline gap-1">
              <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6 text-ink" />
              <span className="font-serif text-5xl sm:text-6xl text-ink font-bold tracking-tight tabular-nums">{yearly ? clinicY : clinicM}</span>
              <span className="text-ink-mid text-sm sm:text-base font-medium">/mo</span>
            </div>
            {yearly && <div className="text-xs text-pine font-bold mt-1">Billed ₹14,999/year · save ₹2,989</div>}
            <p className="text-sm text-ink-mid font-medium mt-4 leading-relaxed">For multi-dentist practices ready to scale.</p>
            <ul className="mt-7 sm:mt-8 space-y-3 text-sm">
              {["Everything in Solo, plus:", "Unlimited team members & roles", "WhatsApp reminders (unlimited)", "50 GB X-ray storage",
                "Viyora Copilot (unlimited)", "Vapi voice AI receptionist", "Treatment Kits & inventory automation",
                "Priority WhatsApp support"].map((f, i) => (
                <li key={f} className={i === 0 ? "text-ink-soft italic text-xs font-semibold uppercase tracking-wider mb-1" : "flex items-start gap-3 text-ink-mid font-medium leading-relaxed"}>
                  {i > 0 && (
                    <span className="mt-0.5 grid place-items-center w-4 h-4 rounded-full shrink-0 bg-pine text-white">
                      <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                    </span>
                  )}
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-clinic-btn"
              onClick={() => subscribe(yearly ? "clinic_yearly" : "clinic_monthly")}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white px-5 py-3 text-sm font-semibold hover:bg-ink-mid transition-colors shadow-sm">
              Start free trial
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-20 sm:py-24 lg:py-32" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-6">
        <motion.div {...fadeUp} className="text-center mb-10 sm:mb-14">
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-medium mb-4">FAQ</div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-ink leading-[1.05] tracking-tight">
            Frequently asked questions
          </h2>
        </motion.div>
        <div className="space-y-3">
          {FAQS.map((item, idx) => (
            <motion.details key={item.q} {...stagger(idx)}
              open={open === idx}
              onClick={(e) => { e.preventDefault(); setOpen(open === idx ? -1 : idx); }}
              className="bg-white border border-[#E2DCD0] rounded-2xl overflow-hidden group"
              data-testid={`faq-${idx}`}>
              <summary className="cursor-pointer list-none p-5 sm:p-6 flex items-center justify-between gap-3 sm:gap-4">
                <span className="font-serif text-base sm:text-lg text-ink leading-snug">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-ink-soft shrink-0 transition-transform ${open === idx ? "rotate-180" : ""}`} />
              </summary>
              {open === idx && (
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-ink/75 text-[14px] sm:text-[15px] leading-relaxed">{item.a}</div>
              )}
            </motion.details>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section id="contact" className="py-20 sm:py-24 lg:py-28 bg-ink relative overflow-hidden" data-testid="final-cta">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(245,83,45,0.18),transparent_55%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(245,83,45,0.10),transparent_50%)] pointer-events-none" />
      <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center relative">
        <motion.div {...fadeUp} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.15em] font-semibold text-white/80 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-pine" /> Get started today
        </motion.div>
        <motion.h2 {...fadeUp} className="font-serif text-4xl sm:text-5xl md:text-6xl text-white leading-[1.05] tracking-tight font-bold">
          The Clinic OS for Modern Practices
        </motion.h2>
        <motion.p {...fadeUp} className="mt-5 sm:mt-6 text-base sm:text-lg text-white/70 font-medium max-w-2xl mx-auto leading-relaxed">
          Viyora — an AI-powered clinic management platform trusted by dentists
          and healthcare practices across India. 30-day free trial. No credit
          card. No commitment.
        </motion.p>
        <motion.div {...fadeUp} className="mt-8 sm:mt-10 flex flex-wrap justify-center gap-3">
          <a href={appUrl("/signup")} data-testid="final-cta-primary"
            className="inline-flex items-center gap-2 rounded-full bg-pine text-white px-7 sm:px-8 py-3.5 sm:py-4 text-[15px] font-bold hover:bg-pine-mid transition-colors shadow-[0_20px_48px_-16px_rgba(245,83,45,0.5)]">
            Start 30-day trial <ArrowUpRight className="w-4 h-4" />
          </a>
          <a href="mailto:admin@viyora.co.in" data-testid="final-cta-demo"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white border border-white/25 px-7 sm:px-8 py-3.5 sm:py-4 text-[15px] font-semibold hover:bg-white/15 transition-colors backdrop-blur-sm">
            Book a demo
          </a>
        </motion.div>

        {/* Contact chips */}
        <motion.div {...fadeUp} className="mt-9 sm:mt-10 flex flex-wrap items-center justify-center gap-3 text-sm">
          <a href="tel:+919810453892" data-testid="cta-phone"
             className="inline-flex items-center gap-2 rounded-full bg-white/5 text-white/85 border border-white/15 px-4 py-2 hover:bg-white/10 hover:text-white transition-colors backdrop-blur-sm">
            <Phone className="w-3.5 h-3.5" /> +91 9810453892
          </a>
          <a href="https://wa.me/919810453892?text=Hi%20Viyora%2C%20I%20have%20a%20question"
             target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
             className="inline-flex items-center gap-2 rounded-full bg-white/5 text-white/85 border border-white/15 px-4 py-2 hover:bg-white/10 hover:text-white transition-colors backdrop-blur-sm">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp us
          </a>
          <a href="mailto:admin@viyora.co.in" data-testid="cta-email"
             className="inline-flex items-center gap-2 rounded-full bg-white/5 text-white/85 border border-white/15 px-4 py-2 hover:bg-white/10 hover:text-white transition-colors backdrop-blur-sm">
            <FileText className="w-3.5 h-3.5" /> admin@viyora.co.in
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-ink text-white/60 py-16 border-t border-white/5" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-10 text-sm">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <img src="/viyora-icon-64.png" alt="Viyora" className="w-8 h-8 " />
            <span className="font-serif text-xl text-white font-semibold">Viyora</span>
          </div>
          <p className="text-white/55 leading-relaxed text-[13px]">
            Viyora is a cloud-based clinic management platform developed and
            operated by Viyora Tech. Viyora helps clinics manage appointments,
            patient records, billing, inventory, WhatsApp communication,
            analytics and AI-powered workflows from one secure platform.
          </p>
          <div className="mt-4 text-[11px] text-white/50 space-y-1">
            <div>Website: <a href="https://viyora.co.in" className="hover:text-white">https://viyora.co.in</a></div>
            <div>Email: <a href="mailto:admin@viyora.co.in" className="hover:text-white">admin@viyora.co.in</a></div>
            <div>Phone: <a href="tel:+919810453892" className="hover:text-white">+91 9810453892</a></div>
            <div>Location: Noida, India</div>
          </div>
        </div>
        <div>
          <div className="text-white font-semibold mb-4 text-[12px] uppercase tracking-[0.15em]">Product</div>
          <ul className="space-y-2.5 text-[13px]">
            <li><span className="text-white">Viyora</span></li>
            <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
            <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
            <li><Link to="/blog" className="hover:text-white transition-colors" data-testid="footer-blog">Blog</Link></li>
            <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
            <li><a href={appUrl("/signup")} className="hover:text-white transition-colors">Start free trial</a></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-semibold mb-4 text-[12px] uppercase tracking-[0.15em]">Legal</div>
          <ul className="space-y-2.5 text-[13px]">
            <li><Link to="/legal/terms" className="hover:text-white transition-colors">Terms of service</Link></li>
            <li><Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy policy</Link></li>
            <li><Link to="/legal/dpdp" className="hover:text-white transition-colors">DPDP compliance</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-semibold mb-4 text-[12px] uppercase tracking-[0.15em]">Contact</div>
          <ul className="space-y-2.5 text-[13px]" data-testid="footer-contact">
            <li><a href="mailto:admin@viyora.co.in" className="hover:text-white transition-colors" data-testid="footer-email">admin@viyora.co.in</a></li>
            <li><a href="tel:+919810453892" className="hover:text-white transition-colors" data-testid="footer-phone">+91 9810453892</a></li>
            <li><a href="https://wa.me/919810453892?text=Hi%20Viyora%2C%20I%20have%20a%20question" target="_blank" rel="noopener noreferrer"
                   className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-whatsapp">
              <span className="inline-block w-2 h-2 rounded-full bg-[#25D366]" />WhatsApp us</a></li>
            <li className="text-white/55">Noida, India</li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-white/10 text-[12px] text-white/50 flex flex-wrap items-center justify-between gap-4">
        <div>© 2026 Viyora Tech. All rights reserved.</div>
        <div>Made with care in India.</div>
      </div>
    </footer>
  );
}

// ---------- Floating contact-support FAB ----------
function ContactSupportFab() {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="fixed z-40 bottom-6 left-6" data-testid="contact-support-widget">
      {open && (
        <div className="mb-3 w-72 rounded-2xl bg-white border border-[#E2DCD0] shadow-xl p-5" data-testid="contact-support-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-serif text-lg text-ink">Talk to us</div>
            <button onClick={() => setOpen(false)} className="text-ink-soft hover:text-ink" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <a href="https://wa.me/919810453892?text=Hi%20Viyora%2C%20I%20have%20a%20question" target="_blank" rel="noopener noreferrer"
             data-testid="contact-support-whatsapp"
             className="w-full flex items-center gap-3 rounded-xl bg-[#25D366] text-white px-4 py-3 hover:bg-[#1DA850] mb-2">
            <MessageCircle className="w-4 h-4" />
            <div><div className="text-sm font-medium">WhatsApp us</div><div className="text-[11px] opacity-90">+91 9810453892</div></div>
          </a>
          <a href="tel:+919810453892" data-testid="contact-support-call"
             className="w-full flex items-center gap-3 rounded-xl bg-pine text-linen px-4 py-3 hover:bg-pine/90 mb-2">
            <Phone className="w-4 h-4" />
            <div><div className="text-sm font-medium">Call us</div><div className="text-[11px] opacity-80">+91 9810453892</div></div>
          </a>
          <a href="mailto:admin@viyora.co.in" data-testid="contact-support-email"
             className="w-full flex items-center gap-3 rounded-xl bg-linen text-ink border border-[#E2DCD0] px-4 py-3 hover:bg-white">
            <FileText className="w-4 h-4" />
            <div><div className="text-sm font-medium">Email</div><div className="text-[11px] text-ink-mid">admin@viyora.co.in</div></div>
          </a>
        </div>
      )}
      <button onClick={() => setOpen((v) => !v)} data-testid="contact-support-fab"
              aria-label="Contact support"
              className="w-14 h-14 rounded-full bg-pine text-linen shadow-lg hover:bg-pine/90 grid place-items-center transition hover:scale-105 active:scale-95">
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>
    </div>
  );
}

